import { useMemo } from "react";

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import ModelsToRunMultiselect from "../../../../../common/components/select/ServiceSelect/ModelsToRunMultiselect";
import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import { useCreateDeviceMake } from "../hooks/useCreateDeviceMake";
import DeviceHasPanTiltCheckboxInput from "../../../../devices/components/DynamicInputs/DeviceHasPanTiltCheckboxInput";
import { useGetDevicesMakesCached } from "../hooks/useGetDevicesMakesCached";

interface IDeviceMakeState {
  name: string;
  model: string;
  type: string;
  rtspTemplate: string;
  hasPanTilt: boolean;
  modelsToRun: string[];
  hasZoom: boolean;
}

const defaultState: IDeviceMakeState = {
  name: "",
  model: "",
  type: "",
  rtspTemplate: "",
  hasPanTilt: true,
  modelsToRun: [],
  hasZoom: true,
};

export const DEVICE_TYPES = [
  "License Plate Reader",
  "Optical",
  "Optical Multisensor",
  "Thermal",
  "CH4 OGI",
  "VOC OGI",
].sort();

const CreateDeviceMake = (): JSX.Element => {
  const { createDeviceMake, loading } = useCreateDeviceMake();
  const { data } = useGetDevicesMakesCached();

  const existingModels = useMemo(() => {
    const models = (data?.getDevicesMakes?.items ?? [])
      .map(d => d?.model?.trim().toLowerCase())
      .filter((m): m is string => Boolean(m));

    return new Set(models);
  }, [data]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<IDeviceMakeState>({
    defaultValues: defaultState,
    mode: "onChange",
  });

  const watchedHasPanTilt = watch("hasPanTilt");
  const watchedModelsToRun = watch("modelsToRun");

  const handleHasPanTiltChange = (_key: string, value: boolean): void => {
    setValue("hasPanTilt", value, { shouldValidate: true });

    setValue("hasZoom", value, { shouldValidate: false });

    setValue("modelsToRun", [], { shouldValidate: true });
  };

  const handleModelsToRunChange = (value: string[]): void => {
    setValue("modelsToRun", value, { shouldValidate: true });
  };

  const onSubmit = async (values: IDeviceMakeState): Promise<void> => {
    await createDeviceMake(values);

    reset(defaultState);
  };

  const isCreateButtonDisabled = loading || !isValid;

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack marginBottom="0.5em" spacing={2} direction="row">
          <Controller
            name="name"
            control={control}
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <TextField
                required
                sx={{
                  height: "68px",
                }}
                fullWidth
                label="Name"
                {...field}
                value={field.value ?? ""}
                error={errors.name !== undefined}
                helperText={errors.name?.message || ""}
              />
            )}
          />
          <Controller
            name="model"
            control={control}
            rules={{
              required: "This field is required",
              validate: value =>
                !existingModels.has((value || "").trim().toLowerCase()) ||
                "Model should be unique",
            }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                sx={{
                  height: "68px",
                }}
                label="Model"
                {...field}
                value={field.value ?? ""}
                error={errors.model !== undefined}
                helperText={errors.model?.message || ""}
              />
            )}
          />
          <Controller
            name="type"
            control={control}
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <FormControl required fullWidth error={errors.type !== undefined}>
                <InputLabel id="device-make-select-label">Type</InputLabel>
                <Select
                  labelId="device-make-select-label"
                  label="Type"
                  value={field.value || ""}
                  onChange={(e): void => field.onChange(e.target.value)}
                >
                  {DEVICE_TYPES.map((type: string): JSX.Element => {
                    return (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="rtspTemplate"
            control={control}
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <TextField
                required
                sx={{
                  height: "68px",
                }}
                fullWidth
                label="RTSP Template"
                {...field}
                value={field.value ?? ""}
                error={errors.rtspTemplate !== undefined}
                helperText={errors.rtspTemplate?.message || ""}
              />
            )}
          />
        </Stack>

        <Box sx={{ marginBottom: "1em", width: "100%", maxWidth: "380px" }}>
          <DeviceHasPanTiltCheckboxInput
            field={"hasPanTilt"}
            label={"The device has Pan/Tilt functionality"}
            hasPanTilt={watchedHasPanTilt}
            setPanTilt={handleHasPanTiltChange}
          />
        </Box>

        {/* Hidden controller to enforce modelsToRun validation for button disabling */}
        <Controller
          name="modelsToRun"
          control={control}
          rules={{
            validate: value =>
              (value?.length ?? 0) > 0 || "Models to run is required",
          }}
          render={() => <></>}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "1.5em 1em",
          }}
        >
          <ModelsToRunMultiselect
            hasPanTilt={watchedHasPanTilt}
            models={watchedModelsToRun}
            setModels={handleModelsToRunChange}
          />

          <StyledLoadingButton
            disabled={isCreateButtonDisabled}
            variant="contained"
            type="submit"
          >
            Create
          </StyledLoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default CreateDeviceMake;
